import { useRef, useState, useEffect } from "react";
import InputForEmail from "./InputForEmail";
import { useEmailContext, type EmailOption } from "../EmailContext";

function ReceiptsHeading({
  fetchEmailOptions,
}: {
  fetchEmailOptions: (currentEmail: string) => Promise<EmailOption[]>;
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { emails, setEmails, showCC, setShowCC, showBCC, setShowBCC } =
    useEmailContext();

  // Show CC/BCC fields when opening if they have values
  useEffect(() => {
    if (open) {
      setShowCC(emails.cc.length > 0);
      setShowBCC(emails.bcc.length > 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    const wrapper = wrapperRef.current;

    if (!wrapper) return;

    // Focus in event (bubbles unlike React's onFocus)
    const handleFocusIn = () => {
      setOpen(true);
    };

    // Focus out event (bubbles unlike React's onBlur)
    const handleFocusOut = (e: FocusEvent) => {
      console.log("🚀 ~ handleFocusOut ~ e:", e);
      const next = e.relatedTarget as HTMLElement | null;
      // Only close if the next focused element is outside the wrapper
      if (
        (next &&
          next.classList &&
          next.classList.contains("option-dropdown")) ||
        (next && wrapper.contains(next))
      )
        return;
      console.log("why this ", next?.classList);

      // Delay closing to allow synchronous DOM removals to refocus correctly
      setTimeout(() => {
        const active = document.activeElement;
        if (
          active &&
          (wrapper.contains(active) ||
            active.classList?.contains("option-dropdown"))
        ) {
          return;
        }
        setOpen(false);
      }, 50);
    };

    wrapper.addEventListener("focusin", handleFocusIn);
    wrapper.addEventListener("focusout", handleFocusOut);

    return () => {
      wrapper.removeEventListener("focusin", handleFocusIn);
      wrapper.removeEventListener("focusout", handleFocusOut);
    };
  }, []);

  // Helper to display email recipients in collapsed view
  const getDisplayText = () => {
    if (
      emails.to.length === 0 &&
      emails.cc.length === 0 &&
      emails.bcc.length === 0
    ) {
      return "Recipients";
    }

    const parts = [];

    // Add TO recipients
    if (emails.to.length > 0) {
      parts.push(emails.to.map((e) => e.email).join(", "));
    }

    // Add CC recipients with "cc:" prefix
    if (emails.cc.length > 0) {
      parts.push(emails.cc.map((e) => e.email).join(", "));
    }

    // Add BCC recipients with "bcc:" prefix
    if (emails.bcc.length > 0) {
      parts.push("Bcc: " + emails.bcc.map((e) => e.email).join(", "));
    }

    return parts.join(", ");
  };

  return (
    <div ref={wrapperRef} tabIndex={-1} className="relative">
      {!open ? (
        <input
          type="text"
          placeholder="Recipients"
          className="input-header"
          value={getDisplayText()}
          readOnly
        />
      ) : (
        <div>
          <InputForEmail
            name="To"
            needCCAndBCC
            setShowCC={setShowCC}
            setShowBCC={setShowBCC}
            showBCC={showBCC}
            showCC={showCC}
            emails={emails.to}
            handleChange={(value) => {
              setEmails((prev) => ({
                ...prev,
                to: typeof value === "function" ? value(prev.to) : value,
              }));
            }}
            fetchOptions={fetchEmailOptions}
          />
          {showBCC ? (
            <InputForEmail
              name="Bcc"
              needCCAndBCC={false}
              setShowCC={setShowCC}
              setShowBCC={setShowBCC}
              showBCC={showBCC}
              showCC={showCC}
              emails={emails.bcc}
              handleChange={(value) => {
                setEmails((prev) => ({
                  ...prev,
                  bcc: typeof value === "function" ? value(prev.bcc) : value,
                }));
              }}
              fetchOptions={fetchEmailOptions}
            />
          ) : null}
          {showCC ? (
            <InputForEmail
              name="Cc"
              needCCAndBCC={false}
              setShowCC={setShowCC}
              setShowBCC={setShowBCC}
              showBCC={showBCC}
              showCC={showCC}
              emails={emails.cc}
              handleChange={(value) => {
                setEmails((prev) => ({
                  ...prev,
                  cc: typeof value === "function" ? value(prev.cc) : value,
                }));
              }}
              fetchOptions={fetchEmailOptions}
            />
          ) : null}
        </div>
      )}
    </div>
  );
}

export default ReceiptsHeading;
